import React, { useState, useEffect } from "react";
import { Button, Card, Col, Row, Form, Alert, Spinner, Modal } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import './Clients.css';

export default function ModifierClient() {
    const navigate = useNavigate();
    const { id } = useParams(); // Récupérer l'ID du client depuis l'URL
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [clientData, setClientData] = useState({
        code_client: '',
        raison_sociale: '',
        adresse: '',
        telephone: '',
        email: '',
        numero_compte: '',
        ice: '',
        patente: '',
        rc: '',
        identifiant_fiscal: '',
        cnss: '',
        plafond_credit: 0,
        solde: 0,
        date_creation: '',
        date_modification: ''
    });

    // Charger les données du client
    useEffect(() => {
        fetchClientDetails();
    }, [id]);

    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const result = await response.json();
            if (result.success) {
                setClientData(result.data);
            } else {
                setError("Client non trouvé");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Gestionnaire de changement de formulaire
    const handleChange = (e) => {
        const { name, value } = e.target;
        setClientData(prev => ({
            ...prev,
            [name]: name === 'plafond_credit' || name === 'solde' ? parseFloat(value) || 0 : value
        }));
    };

    // Soumission du formulaire de mise à jour
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!clientData.raison_sociale) {
            setError("La raison sociale est obligatoire");
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clientData)
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                setSuccess("Client mis à jour avec succès !");
                setTimeout(() => {
                    navigate('/ventes/clients/liste');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    // Suppression du client
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccess("Client supprimé avec succès !");
                setTimeout(() => {
                    navigate('/ventes/clients/liste');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    // Annuler et retourner à la liste
    const handleCancel = () => {
        navigate('/ventes/clients/liste');
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                            <p className="mt-3 text-muted">Chargement des informations du client...</p>
                        </div>
                    </div>
                    <Footer />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                {/* En-tête */}
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <ol className="breadcrumb fs-sm mb-1">
                            <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                            <li className="breadcrumb-item"><Link to="/ventes">Ventes</Link></li>
                            <li className="breadcrumb-item"><Link to="/ventes/clients/liste">Clients</Link></li>
                            <li className="breadcrumb-item active">Modifier client</li>
                        </ol>
                        <h4 className="main-title mb-0 d-flex align-items-center">
                            <i className="ri-pencil-line text-primary me-2"></i>
                            Modifier le client
                        </h4>
                    </div>
                </div>

                <Row className="justify-content-center">
                    <Col xl="10">
                        <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                            {/* En-tête de la carte avec code client */}
                            <Card.Header className="bg-gradient-primary text-white py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <div className="client-avatar-large bg-white text-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                             style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                                            {clientData.raison_sociale?.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h5 className="mb-1 text-white">{clientData.raison_sociale}</h5>
                                            <p className="mb-0 text-white-50">
                                                <i className="ri-barcode-line me-1"></i>
                                                Code: {clientData.code_client}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <small className="text-white-50 d-block">Créé le</small>
                                        <span className="text-white">{new Date(clientData.date_creation).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>
                            </Card.Header>

                            <Card.Body className="p-4">
                                {error && (
                                    <Alert variant="danger" onClose={() => setError("")} dismissible className="rounded-3">
                                        <i className="ri-error-warning-line me-2"></i>
                                        {error}
                                    </Alert>
                                )}
                                {success && (
                                    <Alert variant="success" className="rounded-3">
                                        <i className="ri-checkbox-circle-line me-2"></i>
                                        {success}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <h5 className="mb-4 pb-2 border-bottom">
                                        <i className="ri-information-line text-primary me-2"></i>
                                        Informations générales
                                    </h5>
                                    
                                    <Row className="g-3 mb-4">
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Code client</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="code_client"
                                                    value={clientData.code_client}
                                                    onChange={handleChange}
                                                    disabled
                                                    className="bg-light"
                                                />
                                                <Form.Text className="text-muted">
                                                    Le code client ne peut pas être modifié
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Raison sociale <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="raison_sociale"
                                                    value={clientData.raison_sociale}
                                                    onChange={handleChange}
                                                    required
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Téléphone</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="telephone"
                                                    value={clientData.telephone}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={clientData.email}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="12">
                                            <Form.Group>
                                                <Form.Label>Adresse</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    name="adresse"
                                                    value={clientData.adresse}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <h5 className="mb-4 pb-2 border-bottom">
                                        <i className="ri-briefcase-line text-primary me-2"></i>
                                        Identifiants juridiques
                                    </h5>

                                    <Row className="g-3 mb-4">
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Numéro de compte</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="numero_compte"
                                                    value={clientData.numero_compte}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>ICE</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="ice"
                                                    value={clientData.ice}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Patente</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="patente"
                                                    value={clientData.patente}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>RC</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="rc"
                                                    value={clientData.rc}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Identifiant fiscal</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="identifiant_fiscal"
                                                    value={clientData.identifiant_fiscal}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>CNSS</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="cnss"
                                                    value={clientData.cnss}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <h5 className="mb-4 pb-2 border-bottom">
                                        <i className="ri-bank-card-line text-primary me-2"></i>
                                        Paramètres financiers
                                    </h5>

                                    <Row className="g-3 mb-4">
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Plafond de crédit</Form.Label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light">FCFA</span>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="plafond_credit"
                                                        value={clientData.plafond_credit}
                                                        onChange={handleChange}
                                                        className="border-2"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Solde actuel</Form.Label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light">FCFA</span>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="solde"
                                                        value={clientData.solde}
                                                        onChange={handleChange}
                                                        className="border-2"
                                                    />
                                                </div>
                                                <Form.Text className="text-muted">
                                                    {clientData.solde > 0 ? (
                                                        <span className="text-danger">Client débiteur</span>
                                                    ) : clientData.solde < 0 ? (
                                                        <span className="text-success">Client créditeur</span>
                                                    ) : null}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    {/* Boutons d'action */}
                                    <div className="d-flex justify-content-between gap-3">
                                        <div>
                                            <Button 
                                                variant="danger" 
                                                onClick={() => setShowDeleteModal(true)}
                                                disabled={saving}
                                                className="px-4 py-2 rounded-3"
                                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', border: 'none' }}
                                            >
                                                <i className="ri-delete-bin-line me-2"></i>
                                                Supprimer le client
                                            </Button>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={handleCancel}
                                                disabled={saving}
                                                className="px-5 py-2 rounded-3"
                                            >
                                                Annuler
                                            </Button>
                                            <Button 
                                                variant="primary" 
                                                type="submit"
                                                disabled={saving}
                                                className="px-5 py-2 rounded-3"
                                                style={{ background: 'linear-gradient(135deg, #4361ee, #3a56d4)', border: 'none' }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Enregistrement...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        Enregistrer
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Modal de confirmation de suppression */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="text-danger">
                            <i className="ri-alert-line me-2"></i>
                            Confirmation de suppression
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-3">
                        <p className="mb-0">
                            Êtes-vous sûr de vouloir supprimer le client <strong>{clientData.raison_sociale}</strong> ?
                        </p>
                        <p className="text-muted small mt-2 mb-0">
                            <i className="ri-information-line me-1"></i>
                            Cette action est irréversible. Toutes les données associées à ce client seront définitivement supprimées.
                        </p>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDelete}
                            disabled={deleting}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', border: 'none' }}
                        >
                            {deleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Confirmer la suppression
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            {/* Styles supplémentaires */}
            <style>{`
                .bg-gradient-primary {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                }
                .client-avatar-large {
                    border: 3px solid rgba(255,255,255,0.3);
                    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
                }
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #4361ee;
                    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
                }
                .input-group-text {
                    border-width: 2px;
                    border-color: #e2e8f0;
                    background: #f8fafc;
                }
            `}</style>
        </React.Fragment>
    );
}